'use client'

import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X, Heart, Shield, Clock, Users } from 'lucide-react'
import { useTheme } from '@/contexts/ThemeContext'
import Link from 'next/link'

interface WelcomeModalProps {
  isOpen: boolean
  onClose: () => void
}

const STORAGE_KEY = 'hideWelcomeModal'

export function checkShouldShowWelcome(): boolean {
  if (typeof window === 'undefined') return false

  const hideUntil = localStorage.getItem(STORAGE_KEY)
  if (!hideUntil) return true

  // 영구 숨김 체크
  if (hideUntil === 'forever') return false

  // 오늘 하루 숨김 체크
  const today = new Date().toISOString().split('T')[0]
  return hideUntil !== today
}

export default function WelcomeModal({ isOpen, onClose }: WelcomeModalProps) {
  const { theme } = useTheme()
  const [mounted, setMounted] = useState(false)
  const [hideToday, setHideToday] = useState(false)
  const [hideForever, setHideForever] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  const handleClose = () => {
    if (hideForever) {
      localStorage.setItem(STORAGE_KEY, 'forever')
    } else if (hideToday) {
      const today = new Date().toISOString().split('T')[0]
      localStorage.setItem(STORAGE_KEY, today)
    }
    onClose()
  }

  const handleGoMatching = () => {
    handleClose()
  }

  if (!mounted || !isOpen) return null

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className={`relative w-full max-w-md max-h-[85vh] overflow-y-auto rounded-2xl shadow-2xl ${
        theme === 'light'
          ? 'bg-white'
          : 'bg-gray-800'
      }`}>
        {/* Close Button */}
        <button
          onClick={handleClose}
          className={`absolute top-3 right-3 p-1.5 rounded-full transition-colors z-10 ${
            theme === 'light'
              ? 'hover:bg-gray-100 text-gray-500'
              : 'hover:bg-gray-700 text-gray-400'
          }`}
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header with Wave Background */}
        <div className="relative overflow-hidden rounded-t-2xl">
          <div className="bg-gradient-to-br from-cyan-500 via-blue-500 to-blue-600 px-6 pt-8 pb-12">
            {/* Decorative waves */}
            <div className="absolute bottom-0 left-0 right-0">
              <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-8 fill-white dark:fill-gray-800">
                <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25"/>
                <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5"/>
                <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"/>
              </svg>
            </div>

            {/* Title */}
            <div className="text-center text-white">
              <div className="text-4xl mb-2">👋</div>
              <h2 className="text-xl font-bold mb-1">
                혼자라도 괜찮아요
              </h2>
              <p className="text-lg font-medium text-blue-100">
                오늘다이빙에 오신 걸 환영합니다!
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          {/* Main Message */}
          <div className={`text-sm leading-relaxed mb-5 ${
            theme === 'light' ? 'text-gray-700' : 'text-gray-300'
          }`}>
            <p className="mb-3">
              안녕하세요! 다이빙을 사랑하는 <span className="font-semibold text-blue-600 dark:text-blue-400">오늘다이빙</span> 입니다.
            </p>
            <p className="mb-3">
              <span className={`${theme === 'light' ? 'text-gray-800' : 'text-gray-200'} font-medium`}>
                "당장 오늘, 내일 다이빙 가고 싶은데..."
              </span>
              <br />
              버디를 구하지 못해서 망설였던 적 있으신가요?
            </p>
            <p>
              저 역시 그 아쉬움을 너무 잘 알기에, 다이버들이 눈치 보지 않고 편하게 만날 수 있는
              <span className="font-medium"> 온라인 공간</span>을 직접 만들게 되었습니다.
            </p>
          </div>

          {/* Key Values */}
          <div className={`rounded-xl p-4 mb-5 ${
            theme === 'light' ? 'bg-blue-50' : 'bg-blue-900/20'
          }`}>
            <p className={`text-xs font-semibold mb-3 ${
              theme === 'light' ? 'text-blue-700' : 'text-blue-400'
            }`}>
              오늘다이빙과 함께해요
            </p>
            <div className="space-y-2.5">
              <div className="flex items-center gap-2.5">
                <div className={`p-1.5 rounded-lg ${
                  theme === 'light' ? 'bg-blue-100' : 'bg-blue-800/40'
                }`}>
                  <Users className={`w-4 h-4 ${
                    theme === 'light' ? 'text-blue-600' : 'text-blue-400'
                  }`} />
                </div>
                <span className={`text-xs ${
                  theme === 'light' ? 'text-gray-700' : 'text-gray-300'
                }`}>마음 맞는 버디 찾기</span>
              </div>
              <div className="flex items-center gap-2.5">
                <div className={`p-1.5 rounded-lg ${
                  theme === 'light' ? 'bg-blue-100' : 'bg-blue-800/40'
                }`}>
                  <Shield className={`w-4 h-4 ${
                    theme === 'light' ? 'text-blue-600' : 'text-blue-400'
                  }`} />
                </div>
                <span className={`text-xs ${
                  theme === 'light' ? 'text-gray-700' : 'text-gray-300'
                }`}>안전한 다이빙 파트너십</span>
              </div>
              <div className="flex items-center gap-2.5">
                <div className={`p-1.5 rounded-lg ${
                  theme === 'light' ? 'bg-blue-100' : 'bg-blue-800/40'
                }`}>
                  <Heart className={`w-4 h-4 ${
                    theme === 'light' ? 'text-blue-600' : 'text-blue-400'
                  }`} />
                </div>
                <span className={`text-xs ${
                  theme === 'light' ? 'text-gray-700' : 'text-gray-300'
                }`}>물속 자유를 함께</span>
              </div>
            </div>
          </div>

          {/* Closing Message */}
          <p className={`text-sm text-center mb-5 ${
            theme === 'light' ? 'text-gray-600' : 'text-gray-400'
          }`}>
            여러분의 안전하고 즐거운 다이빙을 응원합니다! 💙
          </p>

          {/* CTA Button */}
          <Link
            href="/matching/new"
            onClick={handleGoMatching}
            className="block w-full py-3 px-4 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold rounded-xl text-center transition-all shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40"
          >
            🤿 버디 구하러 가기
          </Link>

          {/* Notice Link */}
          <Link
            href="/community/notice/b62f7a0c-1e58-4eae-a234-e9b6ba79e29e"
            className={`block text-xs text-center mt-3 underline ${
              theme === 'light' ? 'text-gray-500 hover:text-gray-700' : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            자세한 이용 안내 보기
          </Link>

          {/* Checkbox Options */}
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={hideToday}
                onChange={(e) => {
                  setHideToday(e.target.checked)
                  if (e.target.checked) setHideForever(false)
                }}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className={`text-xs ${
                theme === 'light' ? 'text-gray-600' : 'text-gray-400'
              }`}>
                오늘 하루 그만 보기
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={hideForever}
                onChange={(e) => {
                  setHideForever(e.target.checked)
                  if (e.target.checked) setHideToday(false)
                }}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className={`text-xs ${
                theme === 'light' ? 'text-gray-600' : 'text-gray-400'
              }`}>
                다시 보지 않기
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}
